import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, TextField, CircularProgress, Alert, Tabs, Tab } from "@mui/material";
import { Save } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import systemSettingsService from "../../services/systemSettingsService";
import PageHeader from "../../components/common/PageHeader";
import useUrlState from "../../hooks/useUrlState";
import { slugifyTab } from "../../utils/tabSlug";

const HBL_SAMPLE = {
    hblNumber: "WPI-HBL-000042",
    masterBillNumber: "MAEU123456789",
    bookingNumber: "987654-01",
    issueDate: "7/26/2026",
    freightTerms: "COLLECT",
    numberOfOriginals: "0 - EXPRESS RELEASE",
    issuerName: "Waypoint Imports",
    issuerTin: "TIN-000111222",
    issuerAddress: "1200 Main St, Savannah, GA, 31401",
    issuerPhone: "(912) 555-0100",
    forwardingAgentName: "Waypoint Imports",
    shipperName: "CaribePrinter LLC",
    shipperTin: "TIN-555666777",
    shipperAddress: "500 Industrial Blvd, USA",
    shipperPhone: "(912) 555-0199",
    consigneeName: "Samama Inam",
    consigneeTin: "TIN-987654",
    consigneeAddress: "20 Windward Dr, Savannah, Grand Cayman",
    consigneePhone: "(345) 555-0123",
    notifyParty: "SAME AS CONSIGNEE",
    vesselVoyage: "BF Cartagena / 026W",
    portOfLoading: "Port Everglades, FL",
    portOfDischarge: "George Town, Cayman Islands",
    countryOfOrigin: "USA",
    cargoRows: `<tr><td style="padding:6px 8px;border-bottom:1px solid #eee">Container: XHCU-507608-8&nbsp;&nbsp; Seal: 1149694&nbsp;&nbsp; Type: 40HC(3)<br/>Container: FCIU-706257-6&nbsp;&nbsp; Seal: 1149701&nbsp;&nbsp; Type: 40HC(4)</td><td style="padding:6px 8px;border-bottom:1px solid #eee;font-family:monospace">WP-2026-0042</td><td style="padding:6px 8px;border-bottom:1px solid #eee">Printed materials<br/>Gift bags</td><td style="text-align:right;padding:6px 8px;border-bottom:1px solid #eee">42.00 LB</td><td style="text-align:right;padding:6px 8px;border-bottom:1px solid #eee">3.50 CFT</td></tr>`,
    totalPackages: 2,
    totalWeightLb: "42.00",
    totalWeightKg: "19.05",
    totalCbf: "3.50",
    totalM3: "0.10",
    declaredValue: "350.00",
};

const MANIFEST_SAMPLE = {
    masterBillNumber: "MAEU123456789",
    vesselVoyage: "BF Cartagena / 026W",
    portOfLoading: "Port Everglades, FL",
    portOfDischarge: "George Town, Cayman Islands",
    generatedDate: "7/26/2026",
    // Cover Sheet (page 1 of the real document) — was previously hardcoded
    // in manifestService.js and never previewable here at all; now the
    // same admin-editable field as the cargo table below (page 2).
    freightWarning: "",
    coverSheetRows: `<tr><td style="padding:8px;border-bottom:1px solid #eee">Samama Inam</td><td style="padding:8px;border-bottom:1px solid #eee;font-family:monospace">WPI-HBL-000042</td><td style="text-align:right;padding:8px;border-bottom:1px solid #eee">42.00</td><td style="text-align:right;padding:8px;border-bottom:1px solid #eee">8.34</td><td style="text-align:right;padding:8px;border-bottom:1px solid #eee">$350.00</td><td style="text-align:right;padding:8px;border-bottom:1px solid #eee">$125.40</td></tr>`,
    totalCustomers: 1,
    sumWeight: "42.00",
    sumCbf: "8.34",
    sumCost: "350.00",
    sumFreight: "125.40",
    // Cargo table (page 2 of the real document).
    manifestRows: `<tr><td style="padding:5px 8px;border-bottom:1px solid #eee;font-family:monospace">WP-2026-0042</td><td style="padding:5px 8px;border-bottom:1px solid #eee;font-family:monospace">WPI-HBL-000042</td><td style="padding:5px 8px;border-bottom:1px solid #eee">DR-10021</td><td style="padding:5px 8px;border-bottom:1px solid #eee">Samama Inam</td><td style="padding:5px 8px;border-bottom:1px solid #eee">CaribePrinter LLC</td><td style="padding:5px 8px;border-bottom:1px solid #eee">Printed materials</td><td style="padding:5px 8px;border-bottom:1px solid #eee">Box 1 of 2</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #eee">21.00</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #eee">4.17</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #eee">42.00</td></tr>`,
    totalPackages: 2,
    totalWeightLb: "42.00",
    totalCbf: "8.34",
    totalCarrierWeightLb: "42.00",
    sharedTrackingNote: "",
};

const renderPreview = (html, data) =>
    (html || "").replace(/\{\{\s*([\w]+)\s*\}\}/g, (m, key) =>
        Object.prototype.hasOwnProperty.call(data, key) ? data[key] : m,
    );

const TEMPLATE_TABS = [
    {
        label: "House Bill of Lading",
        field: "houseBolTemplateHtml",
        fetchFn: "getHouseBolTemplate",
        sample: HBL_SAMPLE,
        variables: [
            "hblNumber", "masterBillNumber", "bookingNumber", "issueDate", "freightTerms", "numberOfOriginals",
            "issuerName", "issuerTin", "issuerAddress", "issuerPhone", "forwardingAgentName",
            "shipperName", "shipperTin", "shipperAddress", "shipperPhone",
            "consigneeName", "consigneeTin", "consigneeAddress", "consigneePhone", "notifyParty",
            "vesselVoyage", "portOfLoading", "portOfDischarge", "countryOfOrigin",
            "cargoRows", "totalPackages", "totalWeightLb", "totalWeightKg", "totalCbf", "totalM3", "declaredValue",
        ],
        description:
            "Printed per customer, per sailing — one House Bill of Lading covers every shipment that customer has on that sailing. " +
            "The downloaded PDF is drawn separately from this HTML (not converted from it), but its layout is hand-kept in sync with this " +
            "template's sections/fields/wording — editing labels or text here won't change the PDF; ask an engineer for structural changes.",
    },
    {
        label: "Consolidated Manifest",
        field: "manifestTemplateHtml",
        fetchFn: "getManifestTemplate",
        sample: MANIFEST_SAMPLE,
        variables: [
            "masterBillNumber", "vesselVoyage", "portOfLoading", "portOfDischarge", "generatedDate",
            "freightWarning", "coverSheetRows", "totalCustomers", "sumWeight", "sumCbf", "sumCost", "sumFreight",
            "manifestRows", "totalPackages", "totalWeightLb", "totalCbf", "totalCarrierWeightLb", "sharedTrackingNote",
        ],
        description:
            "Printed per sailing — the full two-page document: a Cover Sheet (one row per customer, with their " +
            "proportional freight allocation) followed by the Cargo Manifest (one row per physical box across every " +
            "customer on the sailing). The downloaded PDF is drawn separately from this HTML (not converted from it), " +
            "but its layout is hand-kept in sync with this template's sections/fields/wording — editing labels or text " +
            "here won't change the PDF; ask an engineer for structural changes.",
    },
];

// Module-level (not inline in the component) so useUrlState gets a stable
// reference every render — see that hook's own doc comment for why.
const URL_SCHEMA = {
    tab: { default: "" },
};

const AdminCustomsDocTemplates = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
    const tab = Math.max(0, TEMPLATE_TABS.findIndex((t) => slugifyTab(t.label) === urlState.tab));
    const [html, setHtml] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const activeTab = TEMPLATE_TABS[tab];

    const load = (tabIndex) => {
        setLoading(true);
        const t = TEMPLATE_TABS[tabIndex];
        systemSettingsService[t.fetchFn]()
            .then(({ data }) => setHtml(data.data.templateHtml || ""))
            .catch(() => enqueueSnackbar("Failed to load template", { variant: "error" }))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load(tab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await systemSettingsService.update({ [activeTab.field]: html });
            enqueueSnackbar("Template saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save template", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="House BOL / Manifest Templates"
                subtitle="Customize the HTML used for the House Bill of Lading and the Consolidated Manifest"
            />

            <Tabs
                value={tab}
                onChange={(_, v) => setUrlState({ tab: slugifyTab(TEMPLATE_TABS[v].label) })}
                sx={{ borderBottom: "1px solid", borderColor: "divider" }}
            >
                {TEMPLATE_TABS.map((t) => (
                    <Tab key={t.field} label={t.label} sx={{ textTransform: "none", fontWeight: 600 }} />
                ))}
            </Tabs>

            <Alert severity="info" sx={{ borderRadius: 2 }}>
                {activeTab.description}
            </Alert>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Available variables: {activeTab.variables.map((v) => `{{${v}}}`).join(", ")}
                    </Alert>

                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
                        <Card>
                            <CardContent>
                                <TextField
                                    label="HTML Template"
                                    value={html}
                                    onChange={(e) => setHtml(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={24}
                                    sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSave}
                                    disabled={saving}
                                    sx={{ mt: 2 }}
                                >
                                    {saving ? <CircularProgress size={18} color="inherit" /> : "Save Template"}
                                </Button>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                                    Preview (sample data)
                                </Typography>
                                {/* Always white/dark-text, independent of app theme — this
                                    previews what actually PRINTS (always white paper), and
                                    the injected HTML assumes a white backdrop of its own. */}
                                <Box
                                    sx={{
                                        border: "1px solid #ddd",
                                        borderRadius: 2,
                                        overflow: "auto",
                                        bgcolor: "#fff",
                                        color: "#0E1420",
                                    }}
                                    dangerouslySetInnerHTML={{ __html: renderPreview(html, activeTab.sample) }}
                                />
                            </CardContent>
                        </Card>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AdminCustomsDocTemplates;
